import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowUpRight, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, CONTACT, waLink } from "../lib/site";
import { MENU } from "../data/menu";
import { Reveal } from "../components/Reveal";
import { IntroPlane } from "../components/IntroPlane";

const REVIEWS = [
  { name: "Emma Franco", text: "Se siete a Ipsos e cercate la vera cucina italiana, questo è il posto giusto! Piatti abbondanti e deliziosi." },
  { name: "Angelica Raineri", text: "Ristorante tipico italiano con vista sul mare cristallino di Ipsos. Cibo delizioso e personale super disponibile." },
  { name: "Luigi Palma", text: "Un angolo d'Italia autentico! Mamma Rosa conquista con piatti tradizionali preparati con passione e calore." },
  { name: "Carla Di Palma", text: "Mamma Rosa è una garanzia, la loro cucina è ottima. Siamo sempre tornati soddisfatti, la colazione è fantastica." },
  { name: "Giorgia Tario", text: "A dir poco pazzesco. Un'esperienza culinaria indimenticabile sul lungomare di Ipsos, Corfù." },
  { name: "Andrea Ciarla", text: "Prodotti freschi e di alta qualità, sapori autentici. Il caffè è semplicemente straordinario." },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Kicker = ({ n, children }) => (
  <div className="flex items-center gap-4 mb-6" data-testid="section-kicker">
    <span className="font-body text-xs tracking-[0.3em] text-gold">{n}</span>
    <span className="h-px w-10 bg-gold/50" />
    <span className="font-body text-xs tracking-[0.3em] uppercase text-cream/60">{children}</span>
  </div>
);

const Crossfade = ({ images, interval = 4600 }) => {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval]);
  return (
    <div className="absolute inset-0" data-testid="hero-crossfade">
      {images.map((src, idx) => (
        <motion.img
          key={idx}
          src={src}
          alt="Mamma Rosa Corfù"
          initial={false}
          animate={{ opacity: idx === i ? 1 : 0 }}
          transition={{ duration: 2, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ))}
    </div>
  );
};

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroImages = [IMAGES.userPhotos[0], IMAGES.userPhotos[6], IMAGES.userPhotos[1], IMAGES.userPhotos[2]];

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
      <Crossfade images={heroImages} />
      <div className="absolute inset-0 bg-navy-deep/62" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/25 to-navy-deep/55" />

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1100px] mx-auto px-5 md:px-10 flex flex-col items-center text-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="mb-8"
        >
          <img src="/images/logo-light.png" alt="Mamma Rosa" className="h-24 md:h-36 w-auto object-contain" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex items-center justify-center gap-4 mb-7"
        >
          <span className="h-px w-10 bg-gold/60" />
          <span className="font-body text-xs md:text-sm tracking-[0.25em] uppercase text-cream/85">
            {t("hero.kicker")}
          </span>
          <span className="h-px w-10 bg-gold/60" />
        </motion.div>

        <h1 className="font-display font-medium tracking-tight text-cream text-[12vw] leading-[0.94] md:text-[7vw] lg:text-[6.25rem] max-w-[15ch] mx-auto">
          {[t("hero.line1"), t("hero.line2"), t("hero.line3")].map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} custom={i} initial="hidden" animate="show" className="inline-block">
                {i === 2 ? (
                  <>
                    {l.split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="italic text-[#DE2C2C]">{l.split(" ").slice(-1)}</span>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-2xl mx-auto font-body text-cream/80 text-base md:text-xl leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            to="/menu"
            data-testid="hero-menu-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-3.5 text-navy-deep font-body font-semibold text-sm tracking-wide transition-transform hover:scale-[1.03]"
          >
            {t("common.discoverMenu")}
            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-btn"
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3.5 text-cream font-body text-sm tracking-wide hover:border-gold hover:text-gold transition-colors"
          >
            {t("common.bookWhatsapp")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-6 flex items-center justify-center gap-3"
          data-testid="hero-rating"
        >
          <div className="flex text-gold">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
            ))}
          </div>
          <span className="font-body text-sm text-cream/80">
            {CONTACT.rating} · {CONTACT.reviews} Google
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="writing-vertical rotate-180 font-body text-[10px] tracking-[0.3em] uppercase text-cream/50">
          {t("hero.scroll")}
        </span>
        <motion.span
          animate={{ height: [10, 26, 10] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px bg-gold"
        />
      </motion.div>
    </section>
  );
};

const Stats = () => {
  const { t } = useLang();
  const stats = [
    { v: CONTACT.reviews, l: t("stats.reviews") },
    { v: `${CONTACT.rating}★`, l: t("stats.rating") },
    { v: "10+", l: t("stats.cuisine") },
    { v: "2015", l: t("stats.family") },
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="stats">
      {stats.map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className="bg-navy p-8 md:p-10">
          <div className="font-display text-5xl md:text-6xl text-gold tracking-tight">{s.v}</div>
          <div className="mt-3 font-body text-xs md:text-sm tracking-wide uppercase text-cream/60">{s.l}</div>
        </Reveal>
      ))}
    </section>
  );
};

const StoryTeaser = () => {
  const { t } = useLang();
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <Reveal variant="left" className="relative">
        <div className="relative overflow-hidden clip-hero">
          <img src={IMAGES.userPhotos[2]} alt="Mamma Rosa" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="absolute -bottom-6 -right-4 md:right-8 bg-gold text-navy-deep px-6 py-5 clip-tag">
          <div className="font-display text-3xl leading-none">{CONTACT.since}</div>
          <div className="font-body text-[10px] tracking-[0.2em] uppercase mt-1">Family Run</div>
        </div>
      </Reveal>

      <div>
        <Reveal><Kicker n="02">{t("story.kicker")}</Kicker></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-balance">
            {t("story.title")}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-7 font-body text-cream/70 leading-relaxed max-w-xl">{t("story.p1")}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 font-body text-cream/70 leading-relaxed max-w-xl">{t("story.p2")}</p>
        </Reveal>
        <Reveal delay={0.2}>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {t("story.points").map((p, i) => (
              <li key={i} className="flex items-center gap-3 font-body text-cream/85">
                <span className="text-gold">✦</span> {p}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.25}>
          <Link to="/story" data-testid="home-story-link" className="mt-9 inline-flex items-center gap-2 font-body text-sm tracking-wide text-gold link-underline">
            {t("common.readMore")} <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const MenuPreview = () => {
  const { t, lang } = useLang();
  const featured = MENU.filter((c) => ["pizza", "colazione", "primi-mare", "dessert"].includes(c.id));
  return (
    <section className="border-y border-white/10 bg-navy-deep py-20 md:py-32" data-testid="menu-preview">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal><Kicker n="04">{t("menu.kicker")}</Kicker></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">{t("menu.title")}</h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/menu" data-testid="menu-preview-link" className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-6 py-3 font-body text-sm text-cream hover:border-gold hover:text-gold transition-colors">
              {t("common.viewAll")} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {featured.map((cat, i) => (
            <Reveal key={cat.id} delay={i * 0.06} className="bg-navy p-8 md:p-10 group hover:bg-navy-800 transition-colors">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl md:text-3xl tracking-tight text-cream group-hover:text-gold transition-colors">
                  {cat.name[lang] || cat.name.en}
                </h3>
                <span className="font-body text-xs tracking-[0.2em] text-cream/40">
                  0{i + 1}
                </span>
              </div>
              <ul className="mt-6 space-y-3">
                {cat.items.slice(0, 3).map((it, j) => (
                  <li key={j} className="flex items-baseline gap-3 font-body text-sm">
                    <span className="text-cream/85">{it.name}</span>
                    <span className="flex-1 border-b border-dotted border-white/15 translate-y-[-3px]" />
                    <span className="text-gold whitespace-nowrap">{it.price} €</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const GalleryStrip = () => {
  const { t } = useLang();
  const imgs = [IMAGES.userPhotos[0], IMAGES.userPhotos[2], IMAGES.userPhotos[5]];
  return (
    <section className="max-w-[1300px] mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="gallery-strip">
      <div className="text-center mb-16">
        <Reveal>
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="h-px w-10 bg-gold/50" />
            <span className="font-body text-xs tracking-[0.3em] uppercase text-cream/60">{t("gallery.kicker")}</span>
            <span className="h-px w-10 bg-gold/50" />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">{t("gallery.title")}</h2>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-4 md:gap-6">
        {imgs.map((src, i) => (
          <Reveal key={i} variant="fade" delay={i * 0.12}>
            <div className="overflow-hidden">
              <img src={src} alt="Mamma Rosa Corfù" loading="lazy" className="w-full aspect-[3/4] object-cover" />
            </div>
          </Reveal>
        ))}
      </div>
      <div className="text-center mt-14">
        <Reveal>
          <Link to="/gallery" data-testid="gallery-strip-link" className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3 font-body text-sm text-cream hover:border-gold hover:text-gold transition-colors">
            {t("common.viewAll")} <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Reviews = () => {
  const { t } = useLang();
  return (
    <section className="border-y border-white/10 bg-navy-deep py-20 md:py-32" data-testid="reviews">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal><Kicker n="05">{t("reviews.kicker")}</Kicker></Reveal>
            <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-[14ch]">{t("reviews.title")}</h2></Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-4">
              <span className="font-display text-6xl text-gold">{CONTACT.rating}</span>
              <div>
                <div className="flex text-gold">{[0,1,2,3,4].map((s)=>(<Star key={s} size={16} fill="currentColor" strokeWidth={0} />))}</div>
                <div className="font-body text-sm text-cream/60 mt-1">{CONTACT.reviews} {t("reviews.badge")}</div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="border border-white/10 bg-navy p-8 hover:border-gold/40 transition-colors">
              <div className="flex text-gold mb-4">{[0,1,2,3,4].map((s)=>(<Star key={s} size={14} fill="currentColor" strokeWidth={0} />))}</div>
              <p className="font-display text-xl md:text-2xl leading-snug text-cream/90 italic">“{r.text}”</p>
              <div className="mt-6 flex items-center gap-3">
                <span className="w-9 h-9 grid place-items-center rounded-full bg-gold/15 text-gold font-body font-semibold text-sm">{r.name[0]}</span>
                <div>
                  <div className="font-body text-sm text-cream">{r.name}</div>
                  <div className="font-body text-xs text-cream/40">Google · IT</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const LocationCTA = () => {
  const { t } = useLang();
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32 grid lg:grid-cols-2 gap-12 items-stretch" data-testid="home-location">
      <Reveal variant="left" className="relative overflow-hidden min-h-[340px] border border-white/10">
        <iframe
          title="Mamma Rosa map"
          src={CONTACT.mapEmbed}
          className="w-full h-full min-h-[340px] grayscale contrast-125"
          style={{ border: 0, filter: "grayscale(1) invert(0.9) contrast(0.9)" }}
          loading="lazy"
        />
      </Reveal>
      <div className="flex flex-col justify-center">
        <Reveal><Kicker n="06">{t("contact.kicker")}</Kicker></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">{t("contact.book")}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 font-body text-cream/70 max-w-md leading-relaxed">{t("contact.bookSub")}</p></Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex items-start gap-3 font-body text-cream/85">
            <MapPin size={20} className="text-gold mt-0.5" />
            <span>{CONTACT.address}</span>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <a
            href={waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-location-book-btn"
            className="mt-10 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-8 py-4 text-navy-deep font-body font-semibold tracking-wide transition-transform hover:scale-[1.03]"
          >
            {t("common.bookWhatsapp")} <ArrowUpRight size={18} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main>
      <IntroPlane />
      <Hero />
      <Stats />
      <StoryTeaser />
      <GalleryStrip />
      <MenuPreview />
      <Reviews />
      <LocationCTA />
    </main>
  );
}
